/**
 * OnboardingPage
 *
 * Organisation admin page for onboarding new practitioners (and other
 * roles) into the current project. Mirrors the layout of RecordsPage /
 * PractitionersPage: a searchable, paginated table of existing
 * practitioners with an "Onboard Practitioner" action that opens the
 * OnboardingFormModal.
 *
 * The page is intended for users with the `admin` role. It lists the
 * Practitioner FHIR resources in the project and lets the admin create
 * new ones via the backend `/admin/onboard-user` endpoint.
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, Pencil, RefreshCw } from 'lucide-react';
import { Tooltip } from '@/components/base';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  TableView,
  AvatarCell,
  StatusBadge,
  TextCell,
  type TableColumn,
} from '@/components/views/TableView';
import { ONBOARDING_PRACTITIONERS_TABLE_GRID } from '@/components/shared/AppointmentsTable';
import { apiClient } from '@/lib/api/client';
import { useTableFilters } from '@/lib/hooks';
import { PRACTITIONER_STATUS_OPTIONS } from '@/components/shared/DataTable';
import { OnboardingFormModal } from '@/components/modals/OnboardingFormModal';
import {
  EditPractitionerModal,
  type EditPractitionerData,
} from '@/components/modals/EditPractitionerModal';
import {
  ViewPractitionerModal,
  type ViewPractitionerData,
} from '@/components/modals/ViewPractitionerModal';
import { useAuthStore } from '@/lib/stores/authStore';
import { createComponentLogger } from '@/lib/utils/logger';
import { fetchAllPractitionerRoles } from '@/lib/api/practitioner';
import { onboardingApi } from '@/lib/api/onboarding';
import { toast } from 'sonner';

const logger = createComponentLogger('OnboardingPage');

const PAGE_SIZE = 10;

/**
 * Minimal FHIR Practitioner resource shape used by this page.
 */
interface Practitioner {
  id: string;
  resourceType: 'Practitioner';
  active?: boolean;
  name?: Array<{
    given?: string[];
    family?: string;
    text?: string;
  }>;
  identifier?: Array<{
    system?: string;
    value?: string;
  }>;
  telecom?: Array<{
    system?: string;
    value?: string;
    use?: string;
  }>;
  qualification?: Array<{
    code?: {
      text?: string;
    };
  }>;
}

interface PractitionerBundle {
  resourceType: 'Bundle';
  entry?: Array<{
    resource: Practitioner;
  }>;
  total?: number;
  link?: Array<{
    relation: string;
    url: string;
  }>;
}

// ---------- Helpers ----------

const getPractitionerName = (practitioner: Practitioner): string => {
  if (practitioner.name?.[0]) {
    const name = practitioner.name[0];
    if (name.text) return name.text;
    const given = name.given?.join(' ') || '';
    const family = name.family || '';
    return `${given} ${family}`.trim() || 'Unknown';
  }
  return 'Unknown Practitioner';
};

const getPractitionerEmail = (practitioner: Practitioner): string | null => {
  // Prefer work email, then fall back to any email
  const workEmail = practitioner.telecom?.find((t) => t.system === 'email' && t.use === 'work');
  if (workEmail?.value) return workEmail.value;

  // Fall back to any email (get the last one as it's likely the most recent)
  const emails = practitioner.telecom?.filter((t) => t.system === 'email') || [];
  return emails.length > 0 ? emails[emails.length - 1].value || null : null;
};

const getPractitionerPhone = (practitioner: Practitioner): string | null => {
  const phone = practitioner.telecom?.find((t) => t.system === 'phone');
  return phone?.value || null;
};

export function OnboardingPage() {
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = React.useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = React.useState(false);
  const [selectedPractitioner, setSelectedPractitioner] =
    React.useState<EditPractitionerData | null>(null);
  const [viewPractitioner, setViewPractitioner] = React.useState<ViewPractitionerData | null>(null);

  const { activeFilters, setFilters } = useTableFilters({
    onFiltersChange: () => setPage(1),
  });

  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch practitioners (the resources created during onboarding)
  const { data, isLoading, isRefetching, error, refetch } = useQuery({
    queryKey: ['practitioners', debouncedSearch, page, activeFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) {
        params.append('name', debouncedSearch);
      }
      if (activeFilters.active) {
        params.append('active', activeFilters.active as string);
      }
      params.append('_count', String(PAGE_SIZE));
      params.append('_offset', String((page - 1) * PAGE_SIZE));
      const url = `/Practitioner?${params.toString()}`;
      const response = await apiClient.get<PractitionerBundle>(url);
      return response.data;
    },
  });

  const practitioners: Practitioner[] = data?.entry?.map((e) => e.resource) || [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  // Fetch all PractitionerRoles in a single call and map by practitioner ID
  const { data: practitionerRolesData, refetch: refetchPractitionerRoles } = useQuery({
    queryKey: ['practitioner-roles'],
    queryFn: fetchAllPractitionerRoles,
  });

  // Determine whether the current user is allowed to onboard users.
  // Only admins should see the "Onboard Practitioner" action.
  const isAdmin = user?.role === 'admin';

  // Manual cron job trigger (debug — remove after testing)
  const cronJobMutation = useMutation({
    mutationFn: (date?: string) => onboardingApi.triggerCronJob(date),
    onSuccess: (data) => {
      const r = data.result;
      toast.success(
        `Cron done — ${r?.encountersUpdated ?? 0} encounters closed, ${r?.appointmentsUpdated ?? 0} appointments marked noshow` +
          (r?.errors?.length ? ` (${r.errors.length} errors)` : '')
      );
    },
    onError: (err: Error) => {
      toast.error(`Cron job failed: ${err.message}`);
    },
  });

  // Toggle active/inactive mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ practitionerId, isActive }: { practitionerId: string; isActive: boolean }) => {
      return isActive
        ? onboardingApi.deactivateUser(practitionerId)
        : onboardingApi.activateUser(practitionerId);
    },
    onSuccess: (data, variables) => {
      const action = variables.isActive ? 'deactivated' : 'activated';
      toast.success(data.message || `Practitioner ${action} successfully`);
      queryClient.invalidateQueries({ queryKey: ['practitioners'] });
      queryClient.invalidateQueries({ queryKey: ['practitioner-roles'] });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to update practitioner status';
      toast.error(message);
    },
  });

  const handleToggleStatus = (practitioner: Practitioner) => {
    if (!isAdmin) {
      logger.warn('Non-admin user attempted to toggle practitioner status');
      return;
    }
    const isActive = practitioner.active !== false;
    toggleStatusMutation.mutate({ practitionerId: practitioner.id, isActive });
  };

  const handleOpenOnboarding = () => {
    if (!isAdmin) {
      logger.warn('Non-admin user attempted to open onboarding modal');
      return;
    }
    setIsOnboardingModalOpen(true);
  };

  const handleView = (practitioner: Practitioner) => {
    const name = getPractitionerName(practitioner);
    const parts = name.split(' ');
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';

    // Get PractitionerRole data if available
    const roleData = practitionerRolesData?.get(practitioner.id);

    setViewPractitioner({
      id: practitioner.id,
      firstName,
      lastName,
      email: getPractitionerEmail(practitioner),
      phone: getPractitionerPhone(practitioner),
      role: roleData?.roleAuthorization || null,
      specialty:
        [roleData?.specialty, ...(roleData?.additionalSpecialtyLabels ?? [])]
          .filter(Boolean)
          .join(', ') || null,
      active: practitioner.active !== false,
      qualification: practitioner.qualification?.[0]?.code?.text || null,
      practitionerRoleId: roleData?.practitionerRoleId,
    });
    setIsViewModalOpen(true);
  };

  const handleEdit = (practitioner: Practitioner) => {
    if (!isAdmin) {
      logger.warn('Non-admin user attempted to edit practitioner');
      return;
    }
    const name = getPractitionerName(practitioner);
    const parts = name.split(' ');
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';

    // Get PractitionerRole data if available
    const roleData = practitionerRolesData?.get(practitioner.id);

    setSelectedPractitioner({
      id: practitioner.id,
      firstName,
      lastName,
      email: getPractitionerEmail(practitioner) || '',
      phone: getPractitionerPhone(practitioner) || undefined,
      role: roleData?.roleAuthorization || null,
      specialty: roleData?.specialtyCode || undefined,
      additionalSpecialties: roleData?.additionalSpecialties ?? [],
      active: practitioner.active !== false,
    });
    setIsEditModalOpen(true);
  };

  const columns: TableColumn<Practitioner>[] = [
    {
      key: 'name',
      header: 'Practitioner Name',
      sortable: true,
      sortKey: 'name',
      render: (practitioner) => (
        <AvatarCell
          name={getPractitionerName(practitioner)}
          subtitle={getPractitionerEmail(practitioner)}
        />
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (practitioner) => {
        const roleData = practitionerRolesData?.get(practitioner.id);
        return <TextCell>{roleData?.roleAuthorization || '—'}</TextCell>;
      },
    },
    {
      key: 'specialization',
      header: 'Specialization',
      render: (practitioner) => {
        const roleData = practitionerRolesData?.get(practitioner.id);
        return <TextCell>{roleData?.specialty || '—'}</TextCell>;
      },
    },

    {
      key: 'contact',
      header: 'Contact',
      render: (practitioner) => <TextCell>{getPractitionerPhone(practitioner) || '—'}</TextCell>,
    },
    {
      key: 'status',
      header: 'Status',
      filter: {
        type: 'select',
        options: PRACTITIONER_STATUS_OPTIONS,
        value: activeFilters.active as string | undefined,
        onChange: (value) => setFilters({ ...activeFilters, active: value }),
      },
      render: (practitioner) => (
        <div className="text-center">
          <StatusBadge status={practitioner.active === false ? 'inactive' : 'active'} />
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Action',
      render: (practitioner) => (
        <div className="flex items-center justify-end gap-2">
          <Tooltip content="View">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleView(practitioner);
              }}
              className="rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100"
            >
              <Eye className="h-4 w-4" />
            </button>
          </Tooltip>
          <Tooltip content="Edit">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(practitioner);
              }}
              className="rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </Tooltip>
          <Tooltip content={practitioner.active !== false ? 'Deactivate' : 'Activate'}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleStatus(practitioner);
              }}
              disabled={toggleStatusMutation.isPending}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                practitioner.active !== false ? 'bg-green-600' : 'bg-gray-300'
              }`}
              role="switch"
              aria-checked={practitioner.active !== false}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  practitioner.active !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <AppLayout>
      {isAdmin && (
        <div className="flex justify-end px-6 pt-4">
          <button
            onClick={() => cronJobMutation.mutate(undefined)}
            disabled={cronJobMutation.isPending}
            className="flex items-center gap-2 rounded-md border border-orange-300 bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-50"
            title="Manually runs the nightly close-stale-records job — closes open encounters/appointments from prior days"
          >
            <RefreshCw className={`h-4 w-4 ${cronJobMutation.isPending ? 'animate-spin' : ''}`} />
            {cronJobMutation.isPending ? 'Running…' : 'Complete Old Encounters'}
          </button>
        </div>
      )}
      <TableView
        title="Onboard Practitioners"
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: 'Search Practitioner',
        }}
        addButton={
          isAdmin
            ? {
                label: 'Onboard Practitioner',
                onClick: handleOpenOnboarding,
              }
            : undefined
        }
        columns={columns}
        gridTemplate={ONBOARDING_PRACTITIONERS_TABLE_GRID}
        data={practitioners}
        getRowKey={(p) => p.id}
        isLoading={isLoading}
        isRefetching={isRefetching}
        error={error ? 'Failed to load practitioners. Please try again.' : null}
        onRetry={() => {
          refetch();
          refetchPractitionerRoles();
        }}
        onRefresh={() => {
          refetch();
          refetchPractitionerRoles();
        }}
        emptyMessage="No practitioners onboarded yet. Click 'Onboard Practitioner' to add one."
        emptySearchMessage="No practitioners found matching your search."
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          hasPrev,
          hasNext,
          onPrev: () => setPage((p) => Math.max(1, p - 1)),
          onNext: () => setPage((p) => Math.min(totalPages, p + 1)),
        }}
      />

      {/* Onboarding Modal */}
      <OnboardingFormModal
        isOpen={isOnboardingModalOpen}
        onClose={() => setIsOnboardingModalOpen(false)}
        onSuccess={() => {
          refetch();
          refetchPractitionerRoles();
        }}
      />

      {/* Edit Practitioner Modal */}
      <EditPractitionerModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPractitioner(null);
        }}
        practitioner={selectedPractitioner}
      />

      {/* View Practitioner Modal */}
      <ViewPractitionerModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewPractitioner(null);
        }}
        practitioner={viewPractitioner}
      />
    </AppLayout>
  );
}

export default OnboardingPage;
